import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box } from '@mui/material';
import { useDataProvider, useGetIdentity, useRefresh } from 'react-admin';
import { DEFAULTS } from '../../constants';
import { toast } from 'react-toastify';
import { CreateMedicalGroupDialogProps } from '../../utils/DataTypes';

const CreateMedicalGroupDialog: React.FC<CreateMedicalGroupDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedClient,
}) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [medicalGroupNumber, setMedicalGroupNumber] = useState('');
  const [medicalGroupName, setMedicalGroupName] = useState('');
  const [planLocation, setPlanLocation] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const errorMsg = isDuplicate
    ? `Medical Group ${medicalGroupNumber.trim()} already exists for this client. Please edit the existing record instead.`
    : '';

  const isSaveDisabled = !selectedClient || !medicalGroupNumber.trim() || !medicalGroupName.trim() || isDuplicate;

  const handleValidateMedicalGroupNumber = async () => {
    const cleanNumber = medicalGroupNumber.trim();
    if (!selectedClient || !cleanNumber) return false;

    setIsValidating(true);
    try {
      const { data: existing } = await dataProvider.getList('ValidateMedicalGroups', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: selectedClient.billing_client_number,
          medical_group_numbers: [cleanNumber],
        },
      });

      const duplicateFound = existing.length > 0;
      setIsDuplicate(duplicateFound);
      return duplicateFound;
    } catch (error) {
      toast.error('Medical group validation failed');
      return true;
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    const duplicateFound = await handleValidateMedicalGroupNumber();
    if (duplicateFound) return;

    const payload = {
      import_mode: 'append',
      billing_client_name: selectedClient?.billing_client_name || '',
      billing_client_number: selectedClient?.billing_client_number || '',
      medical_groups: [
        {
          medical_group_number: medicalGroupNumber.trim(),
          medical_group_name: medicalGroupName.trim(),
          plan_location: planLocation.trim(),
          created_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
          last_modified_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      ],
    };

    try {
      const { data: result } = await dataProvider.create('BulkAddMedicalGroups', {
        data: payload,
      });

      toast.success(result.message || 'Medical group saved successfully!');
      refresh();
      onClose();
      onSave(payload);
    } catch (error: any) {
      toast.error(`Save failed: ${error.message || 'Server error'}`);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create New Medical Group Crosswalk</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2} mb={1} mt={1}>
          <TextField
            label="Billing Client Name"
            value={selectedClient?.billing_client_name || ''}
            fullWidth
            variant="standard"
            disabled
          />
          <TextField
            label="Client Number"
            value={selectedClient?.billing_client_number || ''}
            variant="standard"
            disabled
            sx={{ width: '200px' }}
          />
        </Box>
        <TextField
          required
          margin="dense"
          label="Medical Group Number"
          type="text"
          fullWidth
          variant="standard"
          value={medicalGroupNumber}
          error={isDuplicate}
          helperText={errorMsg}
          onChange={(e) => {
            setMedicalGroupNumber(e.target.value);
            setIsDuplicate(false);
          }}
          onBlur={handleValidateMedicalGroupNumber}
        />
        <TextField
          required
          margin="dense"
          label="Medical Group Name"
          type="text"
          fullWidth
          variant="standard"
          value={medicalGroupName}
          onChange={(e) => setMedicalGroupName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Plan Location"
          type="text"
          fullWidth
          variant="standard"
          value={planLocation}
          onChange={(e) => setPlanLocation(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={isSaveDisabled}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMedicalGroupDialog;
